// Toasts (IMPLEMENTATION §2.6 "Toasts", R13, UX §2 Toasts / §7): at most toasts.max, toasts.height tall,
// gapAboveCounter px above the counter (or above an open sheet / card), newest at the bottom. An undo toast
// (a counter sale, a Crate "Sell all") has an Undo button (hit.undoW × undoH visual, hit.min hit) that sends
// `undoSell` with the sale's token; when the sim says the undo is no longer possible (the cash was spent,
// the cell is taken, a newer sale) the button gives way to the reason. During a drag every toast fades to
// toasts.dragOpacity and lets the pointer through; its clock stops while a finger is down.
import { canUndoSell } from '../../sim/query.ts';
import { copyText } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import {
  cardHeight,
  galleryCardHeight,
  settingsRect,
  shopSheetHeight,
  toastRects,
} from '../panels.ts';
import { draggingSig, layoutSig, panelSig, sheetSig, toastsSig } from '../state.ts';
import { snapshotSig } from '../store.ts';
import { box } from './box.ts';

export function Toasts() {
  const l = layoutSig.value;
  const list = toastsSig.value.list;
  const dragging = draggingSig.value;
  void snapshotSig.value; // the undo button follows canUndoSell at the snapshot rate
  if (!l || list.length === 0) return null;
  const ctx = shellCtx();
  const ui = ctx.data().ui;
  const copy = ctx.data().copy;
  // the bottom edge: the counter, or the top of what covers it
  let bottom = l.counter.y;
  const p = panelSig.value;
  if (p.k === 'shop' && sheetSig.value === 'open')
    bottom = l.tabs.y - shopSheetHeight(l.Hs, ui.shop);
  else if (p.k === 'settings') bottom = settingsRect(l).y + settingsRect(l).h;
  else if (p.k === 'card') {
    const h = ctx
      .state()
      .buildings.some(
        (b) => p.target.k === 'building' && b.id === p.target.id && b.type === 'gallery',
      )
      ? galleryCardHeight(l.Hs, ui.sheets)
      : cardHeight(l, ui.sheets, p.more);
    bottom = Math.min(bottom, l.tabs.y - h);
  }
  const rects = toastRects(l, list.length, bottom, ui.toasts, ui.layout.gutter);
  const u = canUndoSell(ctx.state(), ctx.data().sim);
  return (
    <div
      class={`toasts ${dragging ? 'dragging' : ''}`}
      style={{ '--drag-opacity': `${ui.toasts.dragOpacity}` }}
    >
      {list.map((t, i) => {
        const r = rects[i];
        if (!r) return null;
        const undoable = t.kind === 'undo' && t.token !== undefined;
        const ok = undoable && u.ok && u.token === t.token;
        return (
          <div
            key={t.id}
            class={`toast toast-${t.kind}`}
            style={box(r)}
            data-testid="toast"
            data-kind={t.kind}
            role="status"
          >
            <span class="toast-text">{t.text}</span>
            {undoable && ok ? (
              <button
                type="button"
                class="btn toast-undo ctl"
                data-hit="toast-undo"
                data-testid="toast-undo"
                style={{ minWidth: `${ui.hit.undoW}px`, height: `${ui.hit.undoH}px` }}
                onClick={() => {
                  ctx.actions.click();
                  const res = ctx.actions.dispatch({ c: 'undoSell', token: t.token as number });
                  if (res.ok) ctx.shell.dismissToast(t.id);
                }}
              >
                {copy.toast.undo ?? 'Undo'}
              </button>
            ) : null}
            {undoable && !ok && !u.ok && u.reason !== 'stale' ? (
              <span class="toast-note">
                {copyText(copy, `toast.${u.reason === 'spent' ? 'spent' : 'noRoom'}`)}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
